import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { formatMessage } from '@99designs/frontend-components/i18n';
import Attribution from '../../../../shared/components/Attribution';
import AvatarWithAttribution from '../../../../shared/components/AvatarWithAttribution';
import Box from '../../../../shared/components/Box';
import DesignCard, { DesignCardMeta } from '../../../../shared/components/DesignCard';
import Heading from '../../../../shared/components/Heading';
import { CTALink } from '../../../../shared/components/Link';
import Scrollable from '../../../../shared/components/Scrollable';
import localizedLowercase from '../../../../shared/lib/localizedLowercase';
import { discoverPath } from '../../../../shared/lib/platformUrls';
import { spaces } from '../../../../shared/lib/styles';
import { UserAvatar } from '../../../../shared/spa/core-components/Avatar';
import {
    ContestCategoryType,
    ContestIndustryType,
    ContestInspiringDesignsType,
} from '../ContestOverviewData';

interface ViewContext {
    title: string;
    ctaCopy: string;
    ctaLink: string;
}

interface Props {
    category: ContestCategoryType;
    industry: ContestIndustryType;
    inspiringDesigns: ContestInspiringDesignsType;
}

function Inspiration({ category, industry, inspiringDesigns }: Props) {
    const { locale } = useIntl();
    const scrollableRef = useRef<Scrollable>(null);

    function isIndustryOther(industryKey: string): boolean {
        return industryKey === 'other';
    }

    function isCategoryOther(categoryKey: string): boolean {
        return !!['other-design', 'other-design-tasks'].find((k) => k === categoryKey);
    }

    function getViewContext(): ViewContext {
        if (industry) {
            const industryTitle: string = localizedLowercase(
                locale,
                isIndustryOther(industry.key)
                    ? __('design')
                    : formatMessage(__('{industryTitle} design'), {
                          industryTitle: industry.title,
                      })
            );
            return {
                title: formatMessage(__('Check out other {categoryOrIndustry} inspiration'), {
                    categoryOrIndustry: industryTitle,
                }),
                ctaCopy: formatMessage(__(`Explore more {categoryOrIndustry} inspiration`), {
                    categoryOrIndustry: industryTitle,
                }),
                ctaLink: `${discoverPath()}?category=${category.key}&industry=${industry.key}`,
            };
        }

        const categoryTitle = isCategoryOther(category.key)
            ? __('design')
            : localizedLowercase(locale, category.title);
        return {
            title: formatMessage(__('Check out other {categoryOrIndustry} inspiration'), {
                categoryOrIndustry: categoryTitle,
            }),
            ctaCopy: formatMessage(__(`Explore more {categoryOrIndustry} inspiration`), {
                categoryOrIndustry: categoryTitle,
            }),
            ctaLink: `${discoverPath()}?category=${category.key}`,
        };
    }

    const viewContext = getViewContext();

    return (
        <>
            <div className="row">
                <div className="column small-12 medium-12 normal-6 large-5">
                    <Box paddingBottom={['space10', 'space10', 'space10', 'space10']}>
                        <Heading type="h2" variations={['h1', 'no-margin']}>
                            {viewContext.title}
                        </Heading>
                    </Box>
                </div>
            </div>

            <Scrollable
                variations={['full-page-width']}
                ref={scrollableRef}
                style={{ paddingBottom: spaces.space8 }}
            >
                {inspiringDesigns.map((design) => (
                    <div key={design.id} style={{ width: 430 }}>
                        <Box paddingBottom={['space7', 'space7', 'space7', 'space7']}>
                            <DesignCard
                                src={design.image}
                                alt={design.title}
                                variations={['lazyload', 'medium-shadow']}
                                designMetaContent={
                                    <DesignCardMeta
                                        designId={design.id}
                                        designTitle={design.title}
                                        designDescription={design.description}
                                        designerId={design.designer.id}
                                    />
                                }
                            />
                        </Box>

                        <AvatarWithAttribution
                            avatar={
                                <UserAvatar
                                    isLinked
                                    userId={design.designer.id}
                                    size="extra-small"
                                    userName={design.designer.displayName}
                                />
                            }
                            attribution={
                                <Attribution name={design.designer.displayName} size="root" />
                            }
                        />
                    </div>
                ))}
            </Scrollable>
            <div className="row">
                <div className="column">
                    <div className="centered-text">
                        <CTALink color="heritage" href={viewContext.ctaLink}>
                            {viewContext.ctaCopy}
                        </CTALink>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Inspiration;
